/* eslint-disable no-loop-func */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from "react";
import { CircularProgress, Tooltip } from "@mui/material";
import { useDrag, useDrop } from "react-dnd";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import "./Folder.scss";
import { GetFoldersByUserIdDto } from "../../types/FolderPermission";
import { FOLDERTREEMESSAGES } from "../../constants/messages";

interface FolderNodeProps {
  folder: GetFoldersByUserIdDto;
  folders: GetFoldersByUserIdDto[];
  onExpand: () => void;
  expanded: boolean;
  onMoveFolder: (draggedId: number, targetId: number | null) => void;
  loading: boolean;
  selectedFolderId: number | null;
  children?: React.ReactNode;
}

const EllipsedText: React.FC<{ text: string; onClick?: () => void }> = ({ text, onClick }) => {
  const spanRef = React.useRef<HTMLSpanElement>(null);
  const [isEllipsed, setIsEllipsed] = React.useState(false);

  React.useEffect(() => {
    if (spanRef.current) {
      const ellipsed = spanRef.current.scrollWidth > spanRef.current.clientWidth;
      setIsEllipsed(ellipsed);
    }
  }, [text]);

  const textElement = (
    <span ref={spanRef} onClick={onClick} className="folder-text-ellipsis">
      {text}
    </span>
  );

  return isEllipsed ? (
    <Tooltip title={text} placement="right" arrow>
      {textElement}
    </Tooltip>
  ) : (
    textElement
  );
};

const FolderNode: React.FC<FolderNodeProps> = ({
  folder,
  folders,
  onExpand,
  expanded,
  onMoveFolder,
  loading,
  selectedFolderId,
  children,
}) => {
  const canEdit = folder.canEdit;

  const [, drag] = useDrag({
    type: FOLDERTREEMESSAGES.DRAG_TYPE,
    item: { folderId: folder.folderId, canEdit: folder.canEdit },
    canDrag: () => canEdit,
  });

  const [{ isOver }, drop] = useDrop({
    accept: FOLDERTREEMESSAGES.DRAG_TYPE,
    canDrop: (item: { folderId: number }) =>
      item.folderId !== folder.folderId && !isDescendant(item.folderId, folder.folderId),

    drop: (item: { folderId: number }, monitor) => {
      if (monitor.didDrop()) return; // Prevent multiple drops
      onMoveFolder(item.folderId, folder.folderId); // Move folder on drop
      return { moved: true };
    },

    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
    }),
  });

  const isDescendant = (draggedId: number, targetId: number): boolean => {
    let parent = folders.find((folder) => folder.folderId === targetId)?.parentFolderId;
    while (parent) {
      if (parent === draggedId) return true;
      parent = folders.find((folder) => folder.folderId === parent)?.parentFolderId;
    }
    return false;
  };

  const handleFolderClick = () => {
    onExpand();
  };

  const dropClass = isOver ? "folder-drop-target" : "folder-normal";

  return (
    <li ref={(node) => drop(node)} className={dropClass}>
      <div
        ref={canEdit ? drag : null}
        onClick={handleFolderClick}
        className={`folder-list-item ${selectedFolderId === folder.folderId ? "selected" : ""}`}
      >
        {folder.childFolderCount > 0 ? (
          expanded ? (
            <>
              <KeyboardArrowDownIcon className="folder-icon folder-arrow" />
              <img
                src={`${selectedFolderId === folder.folderId ? "folder-open-white.svg" : "folder-open.svg"}`}
                alt="folder-icon"
                className="folder-icon"
              />
            </>
          ) : (
            <>
              <KeyboardArrowRightIcon className="folder-icon folder-arrow" />
              <img
                src={`${selectedFolderId === folder.folderId ? "folder-close-white.svg" : "folder-close.svg"}`}
                alt="folder-icon"
                className="folder-icon"
              />
            </>
          )
        ) : (
          <>
            <KeyboardArrowRightIcon className="folder-icon folder-arrow" />
            <img
              src={`${selectedFolderId === folder.folderId ? "folder-open-white.svg" : "folder-open.svg"}`}
              alt="folder-icon"
              className="folder-icon"
            />
          </>
        )}

        {/* Ellipsed text with tooltip */}
        <EllipsedText text={folder.folderName} />

        {loading && <CircularProgress size={16} className="folder-loading d-none" />}
      </div>
      {children}
    </li>
  );
};

export default FolderNode;
